import React from 'react';
import { CheckIcon } from 'lucide-react';
import { CtaButton, CurrencyNote } from '../CtaButton';
import { Accent } from './SectionHeading';
import { BrowLine } from './BrowLine';
import { heroIndicators } from '../../data/content';
import { MOCKUP_URL } from '../../data/images';

/**
 * First screen: headline → subheadline → mockup → mini description → button.
 * Desktop splits into two columns so everything sits above the fold.
 */
export function HeroSection() {
  return <section className="relative overflow-hidden bg-brand-paper px-5 pb-12 pt-7 lg:px-10 lg:pb-14 lg:pt-10" data-id="element-94" data-mp="1ud47hljqzyup">
      <BrowLine className="pointer-events-none absolute -left-16 top-8 hidden h-24 w-[22rem] text-brand-gold/20 lg:block" data-id="element-95" data-mp="1qbab3pgl5h0c" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-x-12 lg:grid-cols-[1fr_1.12fr]" data-id="element-96" data-mp="1lbe6bo02dljq">
        <div className="text-center lg:col-start-1 lg:row-start-1 lg:self-end lg:text-left" data-id="element-97" data-mp="exeyooh2fmct">
          <p className="inline-flex items-center rounded-[8px] border border-brand-gold/40 bg-brand-ivory px-4 py-2 font-body text-[10px] font-bold uppercase tracking-[0.18em] text-brand-charcoal/75 sm:text-[11px]" data-id="element-98" data-mp="9w90wc8jt8wf">
            For Brow Educators &amp; Instructors
          </p>
          <h1 className="mt-5 font-display text-[2.4rem] font-bold leading-[1.02] tracking-[-0.015em] text-brand-charcoal sm:text-[3.2rem] lg:text-[3.5rem] xl:text-[3.9rem]" data-id="element-99" data-mp="2zg69kfre0dqr">
            Prepare Your Brow Classes <Accent data-id="element-100" data-mp="2ppbm582en83q">in 5 Minutes</Accent> — Instead of Spending Hours.
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-[15px] leading-relaxed text-brand-charcoal/70 sm:text-[16px] lg:mx-0" data-id="element-101" data-mp="2uga5kgiowmb7">
            Brow Instructor Ready™ gives you a complete, structured education system to help you
            teach mapping, shaping, corrective design, color, henna, lamination, consultation and more
            — without starting from a blank page every time you teach.
          </p>
        </div>

        <div className="relative mx-auto mt-7 w-full max-w-[620px] lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:mt-0 lg:max-w-none" data-id="element-102" data-mp="2bqbzu72d75aq">
          <img src={MOCKUP_URL} alt="Brow Instructor Ready — six pastel volumes shown as books, tablet, laptop and phone" width={1536} height={1024} loading="eager" decoding="async" className="relative block w-full object-contain" style={{
          filter: 'drop-shadow(0 34px 40px rgba(120, 96, 56, 0.22))'
        }} data-id="element-103" data-mp="hb551zvocd09" />
        </div>

        <div className="mt-7 text-center lg:col-start-1 lg:row-start-2 lg:mt-6 lg:self-start lg:text-left" data-id="element-104" data-mp="106dep1k2ffbi">
          <p className="mx-auto max-w-xl font-display text-[1.35rem] font-bold leading-snug text-brand-charcoal sm:text-[1.55rem] lg:mx-0" data-id="element-105" data-mp="r81f4a18176x">
            Everything you need to go from “I still have to prepare my class” to{' '}
            <Accent data-id="element-106" data-mp="3byiorvnp6mou">“My class is ready.”</Accent>
          </p>
          <div className="mt-5" data-id="element-107" data-mp="3bvw9tayh94wl">
            <CurrencyNote data-id="element-108" data-mp="36ywcbofp1ah3" />
          </div>
          <CtaButton label="Yes! I Want to Prepare Classes Faster" className="mt-3 w-full sm:w-auto sm:min-w-[420px]" data-id="element-109" data-mp="36xupkmpbezjj" />
          <ul className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 lg:justify-start" role="list" data-id="element-110" data-mp="2wteahvjntmgs">
            {heroIndicators.map(item => <li key={item} className="flex items-center gap-1.5 font-body text-[12px] font-semibold text-brand-charcoal/70" data-id="element-111" data-mp="36sn0kdbemgxv">
                <CheckIcon className="h-3.5 w-3.5 text-brand-cta" strokeWidth={2.5} aria-hidden="true" data-id="element-112" data-mp="3blg8tb6307ep" />
                {item}
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
}